import axios from 'axios';
import { trackException, trackInfo, LogLevel } from '../services/appInsights';
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from '../services/appInsightsConstants';


const axiosInstanceSeascape = axios.create({
  baseURL: window.config.MSI_API_URL,
  withCredentials: true, // enables sending cookies
   headers: {
    'X-App-Client': 'MSI.ProductSelection.App'
  }
});

axios.defaults.withCredentials = true;

// Request interceptor - minimal logging for critical errors only
axiosInstanceSeascape.interceptors.request.use(
  config => config, // No routine request logging
  error => {
    // Only log unexpected request setup errors
    trackException(
      error,
      LogLevel.Error,
      {
        component: AppInsightsComponents.AXIOS_SEASCAPE_INSTANCE,
        action: AppInsightsActions.API_REQUEST_FAILURE,
        errorType: AppInsightsErrorTypes.API_ERROR,
        errorMessage: error.message
      }
    );
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh and critical error handling
axiosInstanceSeascape.interceptors.response.use(
  res => res, // No routine response logging
  async err => {
    const originalRequest = err.config;
    
    // Only log unexpected errors (not routine 4xx client errors)
    const status = err.response?.status;
    if (!status || status >= 500 || (status !== 401 && status !== 403 && status !== 404)) {
      trackException(
        err,
        LogLevel.Error,
        {
          component: AppInsightsComponents.AXIOS_SEASCAPE_INSTANCE,
          action: AppInsightsActions.API_RESPONSE_ERROR,
          errorType: AppInsightsErrorTypes.API_ERROR,
          status: status || 0,
          method: err.config?.method?.toUpperCase() || 'UNKNOWN',
          url: err.config?.url || 'unknown',
          errorMessage: err.message
        }
      );
    }

    if (err.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        trackInfo('Attempting token refresh for Seascape API', {
          component: AppInsightsComponents.AXIOS_SEASCAPE_INSTANCE,
          action: AppInsightsActions.TOKEN_REFRESH_ATTEMPT
        });

        await axios.post(window.config.HORIZON_API_URL + "/token/refresh", {}, {
          withCredentials: true,
        });

        trackInfo('Token refresh successful for Seascape API', {
          component: AppInsightsComponents.AXIOS_SEASCAPE_INSTANCE,
          action: AppInsightsActions.TOKEN_REFRESH_SUCCESS
        });
        
        return axiosInstanceSeascape(originalRequest);
      } catch (refreshError) {
        trackException(
          refreshError as Error,
          LogLevel.Error,
          {
            component: AppInsightsComponents.AXIOS_SEASCAPE_INSTANCE,
            action: AppInsightsActions.TOKEN_REFRESH_FAILURE,
            errorType: AppInsightsErrorTypes.AUTH_ERROR,
            errorMessage: (refreshError as Error).message
          }
        );
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

export default axiosInstanceSeascape;